import React, { useMemo, useState } from "react";
import { Control, FieldValues } from "react-hook-form";
import { debounce } from "lodash";

import Grid from "@mui/material/Grid";
import CircularProgress from "@mui/material/CircularProgress";

import { AutocompleteCloseReason } from "@mui/base";
import { useTheme } from "@mui/material/styles";

import { Toast } from "@covertree/utils";
import { FormAutocomplete, SelectOption } from "@covertree/ui/forms";

import { useGetAutocompleteAddressLazyQuery } from "@covertree/new-business-graphql";

import {
  PreciselyAddressWithUnit,
  areEqual,
  filterOptions,
  locationsToOptions,
} from "./utils";

const PRECISELY_DATA_FETCH_DEBOUNCE = 1000;

interface Props<TValues extends FieldValues, TOptionType> {
  id: string;
  name: string;
  control: Control<TValues>;
  label: React.ReactNode;
  placeholder?: string;
  dataTestId?: string;
  zipCode?: string;
  state?: string;
  tooltip?: string;
  disabled?: boolean;
  onAutocompleteChange?: (
    event: React.SyntheticEvent<Element, Event>,
    value: SelectOption<TOptionType> | null
  ) => void;
  onClose?: (
    event: React.SyntheticEvent,
    reason: AutocompleteCloseReason
  ) => void;
}

export function AddressAutocompletePrecisely<
  TValues extends FieldValues,
  TOptionType,
>({
  id,
  name,
  control,
  label,
  placeholder,
  dataTestId,
  zipCode,
  state,
  tooltip,
  disabled,
  onAutocompleteChange,
  onClose,
}: Props<TValues, TOptionType>) {
  const theme = useTheme();

  const [options, setOptions] = useState<
    SelectOption<PreciselyAddressWithUnit>[]
  >([]);

  const [getAutocompleteAddress, { loading }] =
    useGetAutocompleteAddressLazyQuery();

  const getNewOptions = useMemo(
    () =>
      debounce(async (searchText: string) => {
        if (searchText && searchText.length >= 3) {
          const result = await getAutocompleteAddress({
            variables: { searchText },
          });

          if (result.error) {
            Toast.showError(
              "We couldn't find any place for provided address. Please, type more address letters."
            );
            return [];
          }

          const locations = result?.data?.getAutocompleteAddresses ?? [];

          if (locations.length > 0) {
            setOptions(locationsToOptions(locations));
          }
        }
      }, PRECISELY_DATA_FETCH_DEBOUNCE),
    [state, zipCode]
  );

  return (
    <FormAutocomplete
      id={id}
      name={name}
      control={control}
      {...(onClose ? { onClose } : {})}
      filterOptions={filterOptions}
      label={label}
      options={options}
      placeholder={placeholder}
      dataTestId={dataTestId}
      disabled={disabled}
      onInputChange={(e: React.ChangeEvent<HTMLInputElement>) =>
        getNewOptions(e.target.value)
      }
      inputStyle={{
        padding: "0px !important",
      }}
      inputProps={{
        sx: {
          padding: "0 13px 0 14px !important",
        },
      }}
      isOptionEqualToValue={areEqual}
      loading={loading}
      tooltip={tooltip}
      loadingText={
        <Grid container alignContent="center" justifyContent="center">
          <CircularProgress
            color="secondary"
            data-testid={`${dataTestId}-loading`}
            size={theme.spacing(3.5)}
          />
        </Grid>
      }
      /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
      onChange={onAutocompleteChange as any}
    />
  );
}
